import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Upload, Loader2, CheckCircle2, Clock, AlertCircle, Lock, ArrowUpRight } from "lucide-react";
import { api, extractErr } from "@/portal/PortalAuth";
import { card, btn, ghost, inp } from "@/portal/PortalShell";

const DOC_TYPES = [
  { value: "drivers_license", label: "Driver's license" },
  { value: "state_id", label: "State ID" },
  { value: "passport", label: "Passport" },
];
const MAX = 10 * 1024 * 1024;

const STATUS = {
  Approved: { label: "Approved", Icon: CheckCircle2, chip: "bg-[#DCFCE7] text-[#166534]" },
  Verified: { label: "Verified", Icon: CheckCircle2, chip: "bg-[#DCFCE7] text-[#166534]" },
  "Pending Review": { label: "Pending Review", Icon: Clock, chip: "bg-[#FEF3C7] text-[#92400E]" },
  Pending: { label: "Pending Review", Icon: Clock, chip: "bg-[#FEF3C7] text-[#92400E]" },
  "Needs Resubmission": { label: "Needs Resubmission", Icon: AlertCircle, chip: "bg-[#FEF3C7] text-[#92400E]" },
  "Requires Review": { label: "Needs Resubmission", Icon: AlertCircle, chip: "bg-[#FEF3C7] text-[#92400E]" },
  Rejected: { label: "Rejected", Icon: AlertCircle, chip: "bg-[#FEE2E2] text-[#991B1B]" },
  Failed: { label: "Rejected", Icon: AlertCircle, chip: "bg-[#FEE2E2] text-[#991B1B]" },
  "Not Started": { label: "Not started", Icon: Lock, chip: "bg-[#F5F5F4] text-[#57534E]" },
};

export function IdVerification() {
  const [list, setList] = useState(null);
  const load = () => api.get("/guest/id-verification").then(({ data }) => setList(Array.isArray(data) ? data : [])).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  if (list === null) return null;
  if (!list.length) return null;
  return (
    <section className="mt-6" data-testid="id-verification">
      <div className={card}>
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-[#8A9A86] shrink-0 mt-0.5" strokeWidth={1.75} />
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-[#8A9A86] font-medium">Identity verification</p>
            <p className="mt-1 text-sm text-[#44403C] font-light">Verify your government-issued ID to confirm your reservation. Your documents are stored encrypted and private — reviewed only by authorized Masaya staff, never shared, never shown publicly, and deleted after the retention period.</p>
          </div>
        </div>
        <div className="mt-5 space-y-4">
          {list.map((item) => <IdItem key={item.reservation_id} item={item} onDone={load} />)}
        </div>
      </div>
    </section>
  );
}

function IdItem({ item, onDone }) {
  const s = STATUS[item.status] || STATUS["Not Started"];
  const verified = item.verified;
  return (
    <div className="rounded-xl border border-[#E7E5E4] p-4" data-testid={`id-item-${item.reservation_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-[#1C1917]">{item.room_name || "Reservation"} <span className="text-[#78716C]">· {item.reference || item.reservation_id.slice(0, 8)}</span></p>
        <span data-testid={`id-status-${item.reservation_id}`} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${s.chip}`}>
          <s.Icon className="w-3.5 h-3.5" /> {s.label}
        </span>
      </div>
      {item.latest_upload?.review_note && (item.status === "Needs Resubmission" || item.status === "Rejected") && (
        <p className="mt-2 text-xs text-[#92400E]" data-testid={`id-note-${item.reservation_id}`}>Masaya note: {item.latest_upload.review_note}</p>
      )}
      {verified ? (
        <p className="mt-2 text-xs text-[#4A6A48]">Your identity has been verified. No further action is needed.</p>
      ) : item.status === "Pending Review" ? (
        <p className="mt-2 text-xs text-[#78716C]" data-testid={`id-pending-${item.reservation_id}`}>Your uploaded ID is securely submitted and awaiting Masaya review. We'll email you once it's approved. Your ID image is never displayed again after submission.</p>
      ) : (
        <UploadForm item={item} onDone={onDone} />
      )}
    </div>
  );
}

function UploadForm({ item, onDone }) {
  const [docType, setDocType] = useState("drivers_license");
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [busy, setBusy] = useState(false);
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const resubmit = item.status === "Needs Resubmission" || item.status === "Rejected";

  const submit = async (e) => {
    e.preventDefault();
    if (!front && !back) { toast.error("Please add at least the front of your ID (a clear photo or PDF)."); return; }
    const tooBig = [front, back].find((f) => f && f.size > MAX);
    if (tooBig) { toast.error("Each file must be 10 MB or smaller."); return; }
    setBusy(true);
    try {
      for (const [side, file] of [["front", front], ["back", back]]) {
        if (!file) continue;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("reservation_id", item.reservation_id);
        fd.append("document_type", docType);
        fd.append("side", side);
        await api.post("/guest/id-verification/upload", fd);
      }
      toast.success("ID uploaded securely. It's now pending Masaya review.");
      setFront(null); setBack(null);
      if (frontRef.current) frontRef.current.value = "";
      if (backRef.current) backRef.current.value = "";
      onDone();
    } catch (err) { toast.error(extractErr(err)); } finally { setBusy(false); }
  };

  const fileCls = "text-xs text-[#44403C] file:mr-3 file:rounded-full file:border-0 file:bg-[#1C1917] file:text-white file:px-3 file:py-2 file:text-xs";
  return (
    <div className="mt-3 space-y-3">
      <Link to={`/pay/${item.reservation_id}`} data-testid={`id-stripe-link-${item.reservation_id}`}
        className="inline-flex items-center gap-2 rounded-full bg-white border border-[#E7E5E4] text-[#1C1917] px-4 py-2 text-xs font-medium hover:bg-[#F5F5F4] transition-colors">
        Prefer instant verification? Open your secure pay page <ArrowUpRight className="w-3.5 h-3.5" />
      </Link>
      <form onSubmit={submit} className="rounded-xl bg-[#FAFAF9] border border-[#E7E5E4] p-3 space-y-3" data-testid={`id-upload-form-${item.reservation_id}`}>
        <p className="text-xs text-[#1C1917] font-medium">{resubmit ? "Upload a new ID for review" : "Upload a government ID for manual review"}</p>
        <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid={`id-doc-type-${item.reservation_id}`} className={inp + " h-10"}>
          {DOC_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
        <label className="block">
          <span className="text-xs text-[#44403C]">Front of ID (required)</span>
          <input ref={frontRef} type="file" accept="image/*,application/pdf" onChange={(e) => setFront(e.target.files?.[0] || null)}
            data-testid={`id-front-${item.reservation_id}`} className={"mt-1 w-full " + fileCls} />
        </label>
        <label className="block">
          <span className="text-xs text-[#44403C]">Back of ID (recommended)</span>
          <input ref={backRef} type="file" accept="image/*,application/pdf" onChange={(e) => setBack(e.target.files?.[0] || null)}
            data-testid={`id-back-${item.reservation_id}`} className={"mt-1 w-full " + fileCls} />
        </label>
        <p className="text-[11px] text-[#78716C]">From your phone camera or photo library. Accepted: JPEG, PNG, WEBP or PDF, up to 10 MB each. Stored encrypted and private.</p>
        <button type="submit" disabled={busy} data-testid={`id-upload-submit-${item.reservation_id}`} className={btn + " text-xs"}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {busy ? "Uploading…" : "Upload ID securely"}
        </button>
      </form>
    </div>
  );
}
